/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { TFunction } from 'i18next';
import { ReactElement } from 'react';

import BackupIcon from '@mui/icons-material/Backup';
import NodesIcon from '@mui/icons-material/Hub';
import OverviewIcon from '@mui/icons-material/Info';
import LayersIcon from '@mui/icons-material/Layers';
import PlanIcon from '@mui/icons-material/Memory';
import ApplicationIcon from '@mui/icons-material/Monitor';
import AclIcon from '@mui/icons-material/PermIdentity';
import AdviceIcon from '@mui/icons-material/TipsAndUpdates';
import ParametersIcon from '@mui/icons-material/Tune';

import { Endpoints, Infer, ServiceTypeMatrix } from 'shared';

import { ClusterPageTabs, tabToRouteBinding } from './ClusterPage.consts';

type TabToRouteBindingType = typeof tabToRouteBinding;

export type TabConfigItem = {
  key: string;
  label: (t: TFunction) => string;
  icon?: ReactElement<any>;
  value: (baseUrl: string, getPathForTab: (tab: ClusterPageTabs) => string) => string;
  to: (tabToRouteBinding: TabToRouteBindingType, getPathForTab: (tab: ClusterPageTabs) => string) => string;
  dataCy?: string;
  className?: (flags: boolean, cx: typeof import('classnames')) => string;
  show: (cluster: Infer<typeof Endpoints.GetCluster>['Response']['200']) => boolean;
};

export const TAB_CONFIG: TabConfigItem[] = [
  {
    key: 'application',
    label: t => t('details.application.title'),
    icon: <ApplicationIcon />,
    value: baseUrl => baseUrl,
    to: tabToRouteBinding => tabToRouteBinding[ClusterPageTabs.Application],
    dataCy: 'cluster-page-application-tab',
    show: cluster => ServiceTypeMatrix.displayApplication[cluster.type],
  },
  {
    key: 'overview',
    label: t => t('details.summary.title'),
    icon: <OverviewIcon />,
    value: (baseUrl, getPathForTab) => getPathForTab(ClusterPageTabs.Overview),
    to: (tabToRouteBinding, getPathForTab) => getPathForTab(ClusterPageTabs.Overview),
    dataCy: 'cluster-page-summary-tab',
    show: cluster => ServiceTypeMatrix.displayClusterCredentials[cluster.type],
  },
  {
    key: 'plan',
    label: t => t('plan.title'),
    icon: <PlanIcon />,
    value: (baseUrl, getPathForTab) => getPathForTab(ClusterPageTabs.Plan),
    to: (tabToRouteBinding, getPathForTab) => getPathForTab(ClusterPageTabs.Plan),
    dataCy: 'cluster-page-plan-tab',
    show: () => true,
  },
  {
    key: 'advice',
    label: t => t('advice.title'),
    icon: <AdviceIcon />,
    value: (baseUrl, getPathForTab) => getPathForTab(ClusterPageTabs.Advice),
    to: (tabToRouteBinding, getPathForTab) => getPathForTab(ClusterPageTabs.Advice),
    dataCy: 'cluster-page-advice-tab',
    show: cluster => ServiceTypeMatrix.displayAdvice[cluster.type],
  },
  {
    key: 'parameters',
    label: t => t('details.parameters.title'),
    icon: <ParametersIcon />,
    value: (baseUrl, getPathForTab) => getPathForTab(ClusterPageTabs.Parameters),
    to: (tabToRouteBinding, getPathForTab) => getPathForTab(ClusterPageTabs.Parameters),
    dataCy: 'cluster-page-parameters-tab',
    show: cluster => ServiceTypeMatrix.displayParameters[cluster.type],
  },
  {
    key: 'acl',
    label: t => t('details.acl.title'),
    icon: <AclIcon />,
    value: (baseUrl, getPathForTab) => getPathForTab(ClusterPageTabs.ACL),
    to: (tabToRouteBinding, getPathForTab) => getPathForTab(ClusterPageTabs.ACL),
    dataCy: 'cluster-page-acl-tab',
    show: cluster => ServiceTypeMatrix.displayAcl[cluster.type],
  },
  {
    key: 'replicas',
    label: t => t('details.replicas.title'),
    icon: <NodesIcon />,
    value: (baseUrl, getPathForTab) => getPathForTab(ClusterPageTabs.Replicas),
    to: (tabToRouteBinding, getPathForTab) => getPathForTab(ClusterPageTabs.Replicas),
    dataCy: 'cluster-page-nodes-tab',
    show: cluster => ServiceTypeMatrix.displayReplicas[cluster.type],
  },
  {
    key: 'backups',
    label: t => t('details.backups.title'),
    icon: <BackupIcon />,
    value: (baseUrl, getPathForTab) => getPathForTab(ClusterPageTabs.Backups),
    to: (tabToRouteBinding, getPathForTab) => getPathForTab(ClusterPageTabs.Backups),
    dataCy: 'cluster-page-backups-tab',
    show: cluster => ServiceTypeMatrix.displayBackups[cluster.type],
  },
  {
    key: 'datasets',
    label: t => t('details.datasets.title'),
    icon: <LayersIcon />,
    value: (baseUrl, getPathForTab) => getPathForTab(ClusterPageTabs.Datasets),
    to: (tabToRouteBinding, getPathForTab) => getPathForTab(ClusterPageTabs.Datasets),
    dataCy: 'cluster-page-datasets-tab',
    className: (shouldHideDatasetTab, cx) => cx({ 'd-none': shouldHideDatasetTab }),
    show: cluster => ServiceTypeMatrix.displayDatasets[cluster.type],
  },
];
